import { useTranslation } from 'react-i18next'
import { PricingFeatureKey } from '../../../helpers/pricingTiers'
import { IntervalType, PricingPlan } from '../../../interfaces/pricingPlan'
import { Card, CardContent, CardFooter, CardHeader } from '../../UI/Card'
import GhostButton from '../../Buttons/GhostButton'
import Button from '../../Buttons/Button'
import IconCheck from '../../Icons/IconCheck'
import { cn } from '../../../utils/cn'
import useChangePlan from '../../../services/subscription/useChangePlan'
import Spinner from '../../UI/Spinner'

interface PlanCardProps {
  isActivePlan: boolean
  billingCycle: IntervalType
  price: number
  planFeatures: { id: string; textKey: string }[]
  subscriptionId: number
  selectedPlan: PricingPlan
  handleChange: (intervalType: IntervalType) => void
  onClose: () => void
}

const PlanCard = ({
  isActivePlan,
  billingCycle,
  price,
  planFeatures,
  subscriptionId,
  selectedPlan,
  handleChange,
  onClose,
}: PlanCardProps) => {
  const { t } = useTranslation()

  const planName = selectedPlan.product.base_name
    .split('-')
    .slice(0, 2)
    .join(' ')

  const { mutate: changePlan, isLoading: isChangingPlan } = useChangePlan()

  return (
    <Card className='w-full max-w-2xl rounded-3xl p-3 md:p-8 bg-white mx-auto gap-0'>
      <CardHeader className='text-center'>
        <div className='flex items-center justify-center gap-2'>
          <h2 className='text-2xl font-bold'>{planName}</h2>
          {isActivePlan && (
            <p className='bg-closedBg text-closedText rounded-lg text-center w-[110px] text-sm py-1 font-medium'>
              {t('activePlan')}
            </p>
          )}
        </div>
        <div className='flex items-center justify-center gap-2 mt-2 mb-5'>
          <div className='flex items-center bg-[#E8F1FF] p-1 rounded-full'>
            <GhostButton
              onClick={() => handleChange('monthly')}
              className={`text-sm rounded-full px-5 transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-white text-primaryBlue shadow-sm hover:bg-white'
                  : 'text-lightGray'
              }`}
              label={t('monthly')}
            />
            <GhostButton
              onClick={() => handleChange('yearly')}
              className={`text-sm rounded-full px-5 transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-white text-primaryBlue shadow-sm hover:bg-white'
                  : 'text-lightGray'
              }`}
              label={t('annually')}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className='flex flex-col items-center px-2'>
        <div className='flex items-center justify-center'>
          <span className='text-4xl md:text-5xl font-bold'>{price}€</span>
          {/*{isAnnual && (*/}
          {/*  <div className="flex flex-col items-start">*/}
          {/*    <span className="text-lg md:text-xl text-muted-foreground line-through">*/}
          {/*      {price}€*/}
          {/*    </span>*/}
          {/*    <p className="text-xs md:text-base text-muted-foreground">*/}
          {/*      {t('vatExclusive')}*/}
          {/*    </p>*/}
          {/*  </div>*/}
          {/*)}*/}
        </div>
        <div className='w-full border-t my-4 border-primaryBlue' />
        <ul className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 w-full mb-4 text-left'>
          {planFeatures.map((feature) => (
            <li key={feature.id} className='flex items-center gap-3'>
              <IconCheck />
              <span className='text-lg md:text-lg'>
                {t(feature.textKey as PricingFeatureKey)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className='flex-col gap-2 pt-4 pb-2'>
        <Button
          disabled={isActivePlan || isChangingPlan}
          type='button'
          text={t('changePlan')}
          className={cn({
            'disabled:bg-[#E1ECFC] disabled:cursor-not-allowed': isActivePlan,
          })}
          onClickHandler={() =>
            changePlan(
              { subscriptionId, planId: selectedPlan.id },
              {
                onSuccess: () => onClose(),
              },
            )
          }
        >
          {isChangingPlan && <Spinner spinner='form' color='#0C75FF' />}
        </Button>
      </CardFooter>
    </Card>
  )
}

export default PlanCard
